import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useServerOfTheDay } from '@/hooks/useServerOfTheDay';
import { getServerDetailPath } from '@/utils/i18nRouting';
import { Crown, Vote } from 'lucide-react';
import { VoteCaptcha } from '@/components/VoteCaptcha';
import { Button } from '@/components/ui/button';
import { generateSEOAltText } from '@/utils/seoAltTextGenerator';
import { OptimizedImage } from '@/components/OptimizedImage';

interface ServerOfTheDayProps {
  gameId?: string;
}

export const ServerOfTheDay: React.FC<ServerOfTheDayProps> = ({ gameId }) => {
  const { t, language } = useLanguage();
  const { data: server, isLoading } = useServerOfTheDay(gameId);

  if (isLoading || !server) return null;

  const serverPath = getServerDetailPath(language, { slug: server.slug || undefined, id: server.id });

  const title = language === 'fr'
    ? 'Serveur aléatoire du Jour'
    : language === 'es'
    ? 'Servidor aleatorio del Día'
    : 'Random Server of the Day';

  const subtitle = language === 'fr'
    ? 'Le serveur le plus voté aujourd\'hui'
    : language === 'es'
    ? 'El servidor más votado hoy'
    : 'The most voted server today';

  return (
    <section className="mb-8" aria-label={title}>
      {/* Title */}
      <div className="text-center mb-4">
        <h2 className="text-lg sm:text-xl font-bold text-amber-400 font-mono flex items-center justify-center gap-2">
          <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
          {title}
          <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
        </h2>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">{subtitle}</p>
      </div>

      {/* Card */}
      <div className="relative overflow-hidden rounded-xl border-2 border-amber-500/40 bg-gradient-to-r from-slate-900 via-amber-950/20 to-slate-900 shadow-[0_0_30px_rgba(245,158,11,0.15)] hover:shadow-[0_0_40px_rgba(245,158,11,0.25)] transition-all duration-500 group">
        {/* Glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-amber-400/10 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Banner background */}
        {server.banner_image_url && (
          <div className="absolute inset-0 opacity-15 group-hover:opacity-20 transition-opacity duration-500">
            <img
              src={server.banner_image_url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900" />
          </div>
        )}

        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 p-4 sm:p-5">
          {/* Logo + Server Info wrapped in Link */}
          <Link to={serverPath} className="flex flex-col sm:flex-row items-center gap-4 flex-1 min-w-0">
            {/* Logo */}
            <div className="shrink-0">
              {server.logo_image_url ? (
                <OptimizedImage
                  src={server.logo_image_url}
                  alt={generateSEOAltText({ type: 'server-logo', name: server.name, language })}
                  imageType="logo"
                  width={80}
                  height={80}
                  loading="eager"
                  priority={true}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border-2 border-amber-500/30 shadow-lg"
                  fallback={
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center border-2 border-amber-500/30">
                      <Crown className="w-8 h-8 text-amber-200" />
                    </div>
                  }
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center border-2 border-amber-500/30">
                  <Crown className="w-8 h-8 text-amber-200" />
                </div>
              )}
            </div>

            {/* Server Info */}
            <div className="flex-1 text-center sm:text-left min-w-0">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                {server.game_logo_url && (
                  <OptimizedImage
                    src={server.game_logo_url}
                    alt={server.game_name}
                    imageType="logo"
                    width={20}
                    height={20}
                    loading="lazy"
                    className="w-5 h-5 rounded object-cover"
                    fallback={<Crown className="w-4 h-4 text-amber-400" />}
                  />
                )}
                <span className="text-xs text-amber-400/80 font-mono uppercase tracking-wider">
                  {server.game_name}
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white truncate group-hover:text-amber-300 transition-colors">
                {server.name}
              </h3>
            </div>
          </Link>

          {/* Vote Button - OUTSIDE the Link to prevent event conflicts */}
          <div className="shrink-0">
            <VoteCaptcha
              serverId={server.id}
              serverName={server.name}
              gameType={server.game_name}
            >
              <Button
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-5 py-2.5 rounded-lg shadow-lg shadow-amber-500/20 transition-all duration-300 hover:scale-105 min-h-[44px]"
              >
                <Vote className="w-4 h-4 mr-2" />
                {t('vote.voteButton')}
              </Button>
            </VoteCaptcha>
          </div>
        </div>

        {/* Animated border glow */}
        <div className="absolute inset-0 rounded-xl border-2 border-amber-400/20 animate-pulse pointer-events-none" style={{ animationDuration: '3s' }} />
      </div>
    </section>
  );
};
